/**
 * Filter used to suppress `PARZIVAL_AUTH_ERROR_PAGE`, `MISSING_AUTH_PAGE_LOADED`, and
 * `INVALID_AUTH_PAGE_*` RUM emissions from traffic that is never a real user attempting to
 * sign in. The auth-page metrics are rates (`errors / sessions * 100`), and their
 * numerators receive events from link-preview crawlers and probe-fuzzer requests that hit
 * the SPA fallback. At low-traffic hours the numerator stays flat while the denominator
 * drops, pushing the auth-page alarms over threshold.
 *
 * All entries are opt-in and evidence-based: they are added only after being observed in
 * access-log analysis. Unrecognized UAs and pathnames stay measured so we don't silently
 * hide real-user cohorts.
 */
/**
 * Link-preview crawlers observed in CloudFront logs. They fetch OG tags without executing
 * JS meaningfully and will never authenticate. Kept intentionally narrow — only add a UA
 * marker here after confirming from access logs that it's a bot, not a user.
 */
const CRAWLER_UA_MARKERS = ['facebookexternalhit', 'pinterestbot'];
/**
 * Pathname patterns observed in the RUM auth-page event stream over a 3-day window where
 * none of the traffic is a real user attempting to sign in — static server metadata files,
 * scanner-fuzzer probe strings, and stray non-router paths that hit the SPA fallback. Only
 * add a pattern here after confirming from RUM logs that it's noise.
 */
const SUPPRESSED_PATH_PATTERNS = [
    // Static-server metadata files that scanners fetch as reconnaissance
    /^\/robots\.txt$/,
    /^\/sitemap\.xml$/,
    // Translation-file paths hit directly (not part of a real user route)
    /^\/translations(\/(undefined)?)?$/,
    // Scanner-fuzzer probe strings observed in the RUM stream
    /^\/nonexisting-page-g$/,
    /^\/appendedtopath(no)?slash$/,
    /^\/sssieddgdpathxsx$/,
    // Framework/tool endpoint probes never served by Painter
    /^\/(mcp|cli)$/,
    /^\/webui\/logoutconfirm\.html$/,
];
/**
 * Returns true when the current request looks like non-user traffic (link-preview crawler
 * or probe on a scanner-only pathname). Call sites should skip emitting alarmed RUM
 * events when this returns true; behavior (rendering, redirects, etc.) is unchanged.
 */
export const isBotTraffic = () => {
    const ua = navigator.userAgent.toLowerCase();
    if (CRAWLER_UA_MARKERS.some((marker) => ua.includes(marker))) {
        return true;
    }
    const pathname = window.location.pathname;
    return SUPPRESSED_PATH_PATTERNS.some((re) => re.test(pathname));
};
